import { MODE_CONFIG, type ExperimentLocale } from './locale-config';
import { getDescopeQueryParams } from '../utils/descopeQueryParams';

export const normalizeLocaleCandidate = (
  candidate?: string
): string | undefined => candidate?.trim().toLowerCase().replace(/_/g, '-');

export const matchLocale = <Locale extends string>(
  candidate: string | undefined,
  aliasMap: Record<string, Locale>,
  supportedLocales: readonly Locale[]
): Locale | undefined => {
  const normalized = normalizeLocaleCandidate(candidate);
  if (!normalized) {
    return undefined;
  }

  const aliasedLocale = aliasMap[normalized];
  if (aliasedLocale) {
    return aliasedLocale;
  }

  const baseLanguage = normalized.split('-')[0] ?? normalized;
  return supportedLocales.find(locale =>
    locale.toLowerCase().startsWith(baseLanguage)
  );
};

export const resolveLocale = <Locale extends string>(
  candidates: string[],
  aliasMap: Record<string, Locale>,
  supportedLocales: readonly Locale[]
): Locale => {
  for (const candidate of candidates) {
    const locale = matchLocale(candidate, aliasMap, supportedLocales);
    if (locale) {
      return locale;
    }
  }

  const fallbackLocale = supportedLocales[0];
  if (!fallbackLocale) {
    throw new Error('At least one supported locale is required.');
  }

  return fallbackLocale;
};

export const getExplicitExperimentLocaleOverride = ():
  ExperimentLocale | undefined => {
  const candidate = getDescopeQueryParams().get('lng');
  const locale = matchLocale(
    candidate ?? undefined,
    MODE_CONFIG.experiment.aliasMap,
    MODE_CONFIG.experiment.supportedLocales
  );

  return locale && locale !== 'en' ? locale : undefined;
};

/**
 * Converts a short locale code to a compound locale string when needed.
 * e.g., 'en' -> 'en-US', 'fr' -> 'fr-FR', 'id' -> 'id-ID'.
 */
export const getCompoundLocale = (locale: string): string => {
  const localeMap: Record<string, string> = {
    en: 'en-US',
    ar: 'ar',
    fr: 'fr-FR',
    id: 'id-ID',
    'pt-BR': 'pt-BR',
    'fr-FR': 'fr-FR',
    'id-ID': 'id-ID',
    'de-DE': 'de-DE',
    'it-IT': 'it-IT',
    'nl-NL': 'nl-NL',
    'ja-JP': 'ja-JP',
    'th-TH': 'th-TH',
    'ko-KR': 'ko-KR',
    'uk-UA': 'uk-UA',
    'tr-TR': 'tr-TR',
    'hi-IN': 'hi-IN',
    'ms-MY': 'ms-MY',
    es: 'es',
  };

  return localeMap[locale] || 'en-US';
};
